import { assertNever } from "@ondo/shared";
import type { WorkspaceRef } from "@ondo/shared";

import { EmptyState } from "../lib/EmptyState.tsx";
import { LoadingScreen } from "../lib/Loading.tsx";
import { NotificationRow } from "./NotificationRow.tsx";
import { useNotificationsController } from "./useNotificationsController.ts";
import {
  NOTIFICATIONS_COPY,
  NOTIFICATION_TABS,
  NOT_HERE,
} from "./notifications.constants.ts";

import "./notifications.css";

/**
 * The collector's inbox: everything addressed to this account, and where to act.
 *
 * The front door and never the room. Each row says what it is and takes the
 * reader to the screen that owns the act — Verifications for something asked of
 * them, the work's record for an answer that has come back. Nothing is answered
 * here, which is why there is no capacity picker, no composer and no evidence in
 * this folder.
 *
 * It is also NOT the activity log, which is a different question: that one is
 * what happened to a record and is read by everyone holding a grant, while this
 * is what was addressed to one person and carries their own read state.
 */
export function Notifications({
  workspace,
  inboxOndoId = null,
}: {
  workspace: WorkspaceRef;
  /** Whose inbox. Null is the reader's own — see `useNotificationsController`. */
  inboxOndoId?: string | null;
}) {
  const { view, chooseTab, open, markAllRead } = useNotificationsController(
    workspace,
    inboxOndoId,
  );

  switch (view.kind) {
    case "loading":
      return <LoadingScreen message={NOTIFICATIONS_COPY.loading} />;

    case "error":
      return (
        <p role="alert" className="notifications__error">
          {view.message}
        </p>
      );

    case "open":
      return (
        <div className="notifications">
          <div className="notifications__banner">
            <p>
              {/* The count first when there is one, which is the mock's "7
                  pending actions" — said here rather than in the shell header,
                  because the header is built by `resolveCollector` and cannot
                  see a screen's data. Same shape as the Verifications banner. */}
              <strong>
                {view.pending ? `${view.pending}. ` : `${NOTIFICATIONS_COPY.lead} `}
              </strong>
              {NOTIFICATIONS_COPY.banner}
            </p>
            <p className="notifications__help">{NOTIFICATIONS_COPY.noObligation}</p>
          </div>

          <ul className="notifications__tiles">
            {view.tiles.map((tile) => (
              <li
                key={tile.id}
                className={`notifications__tile ${
                  tile.count > 0 ? `notifications__tile--${tile.id}` : ""
                }`}
              >
                <p className="notifications__tile-label">{tile.label}</p>
                <p className="notifications__tile-count">{tile.count}</p>
              </li>
            ))}
          </ul>

          <div className="notifications__toolbar">
            <nav className="notifications__tabs" aria-label="Notifications">
              {NOTIFICATION_TABS.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  aria-current={view.tab === entry.id}
                  className={`notifications__tab ${
                    view.tab === entry.id ? "notifications__tab--on" : ""
                  }`}
                  onClick={() => chooseTab(entry.id)}
                >
                  {entry.label} · {view.counts[entry.id]}
                </button>
              ))}
            </nav>

            {view.unread > 0 && (
              <button
                type="button"
                className="notifications__mark-all"
                onClick={markAllRead}
              >
                {NOTIFICATIONS_COPY.markAllRead}
              </button>
            )}
          </div>

          {view.empty ? (
            <EmptyState className="notifications__empty" lead={view.empty.lead} detail={view.empty.detail} />
          ) : (
            <ul className="notifications__list">
              {view.rows.map((row) => (
                <NotificationRow key={row.id} row={row} onOpen={() => open(row.id)} />
              ))}
            </ul>
          )}

          {/* Said at the bottom rather than left out. See `NOT_HERE`. */}
          <div className="notifications__absent">
            <p className="notifications__absent-lead">{NOT_HERE.lead}</p>
            <p>{NOT_HERE.detail}</p>
          </div>
        </div>
      );

    default:
      return assertNever(view);
  }
}
